// Mixina zarządzająca domyślnymi filtrami per spaceId + stanami ładowań,
// zakładająca JEDEN obiekt `filters` w komponencie.
//
// Wymagania po stronie komponentu:
// - data(): { filters: {...}, loadingStates: { campaigns: false, groups: false, categories: false } }
// - computed: spaceId, apiParams
// - methods: fetchData() -> Promise, abortRequest()
//
// Zapewnia:
// - zapis domyślnych per spaceId po zejściu loaderów (true -> false) -> snapshot całego `filters`
// - odczyt domyślnych po zmianie spaceId i bez zbędnych reloadów (guard suppressLoad)
// - pojedyncze ładowanie danych per stabilny podpis apiParams (bez dubli)
// - helpery: setLoading(name, value), resetAllLoading(), resetPerSpaceDefaults()

/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */

import debounce from 'lodash/debounce';
import { assignIfChanged } from '@/utils/util';

function createFiltersPerSpace(defaultFilters) {
    const store = new Map();

    function ensure(spaceId) {
        if (!store.has(spaceId)) {
            store.set(spaceId, structuredClone(defaultFilters));
        }
        return store.get(spaceId);
    }

    return {
        init(spaceId) {
            ensure(spaceId);
        },
        get(spaceId, filterName) {
            const bucket = ensure(spaceId);
            return structuredClone(bucket[filterName]);
        },
        set(spaceId, filterName, filterValue) {
            const bucket = ensure(spaceId);
            bucket[filterName] = structuredClone(filterValue);
        },
        getAll(spaceId) {
            return structuredClone(ensure(spaceId));
        },
        setAll(spaceId, partial) {
            const bucket = ensure(spaceId);
            Object.keys(partial || {}).forEach(key => {
                bucket[key] = structuredClone(partial[key]);
            });
        },
        reset(spaceId) {
            store.set(spaceId, structuredClone(defaultFilters));
        },
        clear() {
            store.clear();
        },
    };
}

function normalizeFiltersLikeApi(filtersObj) {
    const src = filtersObj || {};
    return {
        showProductGroupsResults: !!src.showProductGroupsResults,
        campaigns: Array.isArray(src.campaigns) ? [...src.campaigns].sort() : [],
        categories: Array.isArray(src.categories) ? [...src.categories].sort() : [],
        groups: Array.isArray(src.groups) ? [...src.groups].sort() : [],
    };
}

function areLogicalFiltersEqual(aFilters, bFilters) {
    const aNorm = normalizeFiltersLikeApi(aFilters);
    const bNorm = normalizeFiltersLikeApi(bFilters);
    return JSON.stringify(aNorm) === JSON.stringify(bNorm);
}

export function usePerSpaceFilters(
    defaultFilters,
    {
        filtersPath = 'filters',
        loadingStatesPath = 'loadingStates',
        spaceIdPath = 'spaceId',
        apiParamsPath = 'apiParams',
        loadHandlerName = 'fetchData',
        abortHandlerName = 'abortRequest',
        resetHandlerName = 'resetData',
        debounceMs = 150,
        settleDelayMs = 80, // krótka zwłoka po zejściu loaderów, aby dzieci zdążyły z @change
        maxWaitMs = 1200, // maksymalny czas oczekiwania na snapshot, gdy nie ma loaderów (fallback)
    } = {}
) {
    function getByPath(root, path) {
        return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), root);
    }

    function setByPath(root, path, value) {
        const parts = path.split('.');
        const last = parts.pop();
        const target = parts.reduce((acc, key) => acc[key], root);
        target[last] = value;
    }

    function snapshotFilters(ctx) {
        return structuredClone(getByPath(ctx, filtersPath) || {});
    }

    function applyFilters(ctx, next) {
        const current = getByPath(ctx, filtersPath);
        const merged = assignIfChanged(current, structuredClone(next || {}));
        setByPath(ctx, filtersPath, merged);
    }

    function normalizeValue(val) {
        if (Array.isArray(val)) return [...val].sort(); // stabilizacja tablic
        return val;
    }

    function buildApiSignature(ctx) {
        const api = getByPath(ctx, apiParamsPath) || {};
        const { urlParams, requestParams } = api || {};

        // stabilizacja obiektu requestParams (klucze w alfabetycznej kolejności)
        const sortedReq = {};
        Object.keys(requestParams || {})
            .sort()
            .forEach(key => {
                sortedReq[key] = normalizeValue(requestParams[key]);
            });

        // w podpisie znajdą się np. campaigns/products/aggregations, jeśli są w apiParams
        return JSON.stringify({
            spaceId: urlParams?.spaceId ?? null,
            ...sortedReq,
        });
    }

    const manager = createFiltersPerSpace(defaultFilters);

    // --- JEDYNA bramka do uruchamiania ładowania danych (eliminuje dublowanie) ---
    function runFetchIfNeeded(ctx) {
        if (ctx.filtersSuppressLoad || ctx.loadingFilters) return;

        const sig = buildApiSignature(ctx);

        // Jeśli już w trakcie/umówione/załadowane ten sam podpis – nic nie rób
        if (sig === ctx.filtersInFlightSig || sig === ctx.filtersLastScheduledSig || sig === ctx.filtersLastLoadedSig) {
            return;
        }

        // Zarejestruj fakt zaplanowania i natychmiast przejdź do fetch
        ctx.filtersLastScheduledSig = sig;
        ctx.filtersInFlightSig = sig;
        ctx.filtersWaitingLoading = true;

        if (typeof ctx[abortHandlerName] === 'function') {
            ctx[abortHandlerName]();
        }

        let ret;
        if (typeof ctx[loadHandlerName] === 'function') {
            ret = ctx[loadHandlerName]();
        }

        const finalize = () => {
            // Po zakończeniu: oznacz jako ostatnio załadowany, wyczyść in-flight oraz waiting
            ctx.filtersLastLoadedSig = sig;
            if (ctx.filtersInFlightSig === sig) ctx.filtersInFlightSig = '';
            if (ctx.filtersLastScheduledSig === sig) ctx.filtersLastScheduledSig = '';
            ctx.filtersWaitingLoading = false;
        };

        if (ret && typeof ret.finally === 'function') {
            ret.finally(finalize);
        } else {
            finalize();
        }
    }

    // helper – fallback snapshot, gdy nic się nie ładuje, ale nadal czekamy na domyślne
    function snapshotDefaultsIfIdle(ctx) {
        const spaceId = getByPath(ctx, spaceIdPath);
        if (ctx.filtersDefaultsPending && !ctx.loadingFilters) {
            const snap = snapshotFilters(ctx);
            manager.setAll(spaceId, snap);
            ctx.filtersDefaultsPending = false;

            // Po snapshotcie domknij cykl natychmiast (działa także przy pustych filtrach)
            runFetchIfNeeded(ctx);
        }
    }

    // zaplanowanie „stabilizacji” po zejściu loaderów (krótka zwłoka + snapshot)
    function scheduleSettleSnapshot(ctx) {
        if (ctx.filtersSettleTimer) {
            clearTimeout(ctx.filtersSettleTimer);
            ctx.filtersSettleTimer = null;
        }
        ctx.filtersSettleTimer = setTimeout(() => {
            snapshotDefaultsIfIdle(ctx);
        }, settleDelayMs);
    }

    // start maksymalnego okna oczekiwania – jeśli nie będzie żadnego true->false, zrób snapshot po maxWaitMs
    function startMaxWaitTimer(ctx) {
        if (ctx.filtersMaxWaitTimer) {
            clearTimeout(ctx.filtersMaxWaitTimer);
            ctx.filtersMaxWaitTimer = null;
        }
        ctx.filtersMaxWaitTimer = setTimeout(() => {
            snapshotDefaultsIfIdle(ctx);
        }, maxWaitMs);
    }

    return {
        data() {
            return {
                filtersDebouncedLoad: null,
                filtersSuppressLoad: false,
                filtersDefaultsPending: false,
                filtersSettleTimer: null,
                filtersMaxWaitTimer: null,

                // strażnicy dubli:
                filtersLastScheduledSig: '',
                filtersInFlightSig: '',
                filtersLastLoadedSig: '',

                // UI:
                filtersWaitingLoading: false,
            };
        },

        computed: {
            loadingFilters() {
                const bucket = getByPath(this, loadingStatesPath) || {};
                return Object.values(bucket).some(Boolean);
            },
            waitingLoading() {
                return this.filtersWaitingLoading;
            },
            loading() {
                return this.loadingData || this.loadingFilters || this.waitingLoading;
            },
        },

        watch: {
            // Po zejściu loaderów (true -> false) -> po krótkiej zwłoce snapshot i fetch (1 raz)
            loadingFilters(newVal, oldVal) {
                if (newVal === false && oldVal === true && this.filtersDefaultsPending) {
                    scheduleSettleSnapshot(this);
                }
            },

            // Po zmianie spaceId -> przywróć domyślne i domknij cykl
            [spaceIdPath]: {
                immediate: true,
                handler() {
                    const spaceId = getByPath(this, spaceIdPath);

                    // Reset danych przy każdej REALNEJ zmianie spaceId (nie przy pierwszym mountcie)
                    const isFirstInit = !this.filtersDidInitSpace;
                    this.filtersDidInitSpace = true;
                    if (!isFirstInit) {
                        if (typeof this[abortHandlerName] === 'function') this[abortHandlerName]();
                        if (typeof this[resetHandlerName] === 'function') this[resetHandlerName]();
                    }

                    manager.init(spaceId);

                    const saved = manager.getAll(spaceId);
                    this.filtersSuppressLoad = true;
                    applyFilters(this, saved);
                    this.$nextTick(() => {
                        this.filtersSuppressLoad = false;
                        this.filtersDefaultsPending = true;

                        // fallback: jeśli nie pojawi się przejście loaderów, po maxWaitMs zrób snapshot+fetch
                        startMaxWaitTimer(this);

                        // Jeśli nic się nie ładuje (np. puste filtry), domknij cykl natychmiast
                        if (!this.loadingFilters) {
                            snapshotDefaultsIfIdle(this);
                        }
                        // Gdy są loadery – poczekamy na zejście (watcher loadingFilters)
                    });
                },
            },

            // Zmiana apiParams – odpal po debounce, ale finalnie przez JEDNĄ bramkę runFetchIfNeeded
            [apiParamsPath]: {
                immediate: true,
                deep: true,
                handler() {
                    if (!this.filtersDebouncedLoad) {
                        this.filtersDebouncedLoad = debounce(() => {
                            runFetchIfNeeded(this);
                        }, debounceMs);
                    }
                    this.filtersDebouncedLoad();
                },
            },
        },

        methods: {
            setLoading(name, value) {
                const bucket = getByPath(this, loadingStatesPath) || {};
                const prev = bucket[name];
                if (prev !== value) {
                    if (this.$set) this.$set(bucket, name, value);
                    else bucket[name] = value;
                }
                // jeśli zgasł JAKIKOLWIEK loader i to nie jest faza "defaultsPending" → spróbuj domknąć cykl
                if (prev === true && value === false && !this.filtersDefaultsPending) {
                    // opóźnij do następnego ticka, żeby computed apiParams zdążyły się przeliczyć
                    this.$nextTick(() => runFetchIfNeeded(this));
                }
            },

            resetAllLoading() {
                const bucket = getByPath(this, loadingStatesPath) || {};
                Object.keys(bucket).forEach(key => {
                    bucket[key] = false;
                });
            },

            // Reset do domyślnych per spaceId – ładuje TYLKO jeśli podpis się zmienił
            resetPerSpaceDefaults() {
                const spaceId = getByPath(this, spaceIdPath);
                const savedDefaults = manager.getAll(spaceId);

                const currentFilters = getByPath(this, filtersPath);
                if (areLogicalFiltersEqual(currentFilters, savedDefaults)) {
                    return;
                }

                this.filtersSuppressLoad = true;
                applyFilters(this, savedDefaults);

                this.$nextTick(() => {
                    this.filtersSuppressLoad = false;
                    // domknij cykl przez jedyną bramkę
                    runFetchIfNeeded(this);
                });
            },

            // Opcjonalnie: świadomie „zapamiętaj” bieżące ustawienia jako domyślne
            commitDefaultsNow() {
                const spaceId = getByPath(this, spaceIdPath);
                manager.setAll(spaceId, snapshotFilters(this));
                this.filtersDefaultsPending = false;
                if (this.filtersMaxWaitTimer) {
                    clearTimeout(this.filtersMaxWaitTimer);
                    this.filtersMaxWaitTimer = null;
                }
                if (this.filtersSettleTimer) {
                    clearTimeout(this.filtersSettleTimer);
                    this.filtersSettleTimer = null;
                }
            },
        },

        created() {
            const spaceId = getByPath(this, spaceIdPath);
            manager.init(spaceId);

            const saved = manager.getAll(spaceId);
            this.filtersSuppressLoad = true;
            applyFilters(this, saved);
            this.$nextTick(() => {
                this.filtersSuppressLoad = false;
                this.filtersDefaultsPending = true;

                // fallback na wypadek braku przejścia loaderów
                startMaxWaitTimer(this);

                // Gdy od razu brak loaderów – snapshot + pojedynczy fetch
                if (!this.loadingFilters) {
                    snapshotDefaultsIfIdle(this);
                }
            });
        },

        beforeDestroy() {
            if (this.filtersMaxWaitTimer) {
                clearTimeout(this.filtersMaxWaitTimer);
                this.filtersMaxWaitTimer = null;
            }
            if (this.filtersSettleTimer) {
                clearTimeout(this.filtersSettleTimer);
                this.filtersSettleTimer = null;
            }
        },
    };
}
